<template>
  <section class="sale-shared-expenses-tab">
    <section class="sale-shared-expenses-summary-card card">
      <div class="card-body sale-shared-expenses-summary-body">
        <div class="sale-shared-expenses-summary-metrics">
          <article
            v-for="section in summarySections"
            :key="section.key"
            class="sale-shared-expenses-summary-metric"
            :class="`sale-shared-expenses-summary-metric--${section.key}`"
          >
            <div class="sale-shared-expenses-summary-metric-head">
              <h4>{{ section.title }}</h4>
            </div>

            <div class="sale-shared-expenses-summary-breakdown">
              <div class="sale-shared-expenses-summary-breakdown-item sale-shared-expenses-summary-breakdown-item--subtotal">
                <small>{{ text.detail.expensesTab.summary.breakdown.subtotal }}</small>
                <strong>{{ section.values.subtotal }}</strong>
              </div>

              <div class="sale-shared-expenses-summary-breakdown-item sale-shared-expenses-summary-breakdown-item--transferred">
                <small>{{ text.detail.expensesTab.summary.breakdown.transferredTaxes }}</small>
                <strong>{{ section.values.transferred_taxes }}</strong>
              </div>

              <div class="sale-shared-expenses-summary-breakdown-item sale-shared-expenses-summary-breakdown-item--withheld">
                <small>{{ text.detail.expensesTab.summary.breakdown.withheldTaxes }}</small>
                <strong>{{ section.values.withheld_taxes }}</strong>
              </div>

              <div class="sale-shared-expenses-summary-breakdown-item sale-shared-expenses-summary-breakdown-item--total">
                <small>{{ text.detail.expensesTab.summary.breakdown.total }}</small>
                <strong>{{ section.values.total }}</strong>
              </div>
            </div>
          </article>
        </div>

        <div class="sale-shared-expenses-summary-actions">
          <button
            type="button"
            class="app-header-btn app-header-btn--secondary"
            @click="$emit('clear-expenses')"
          >
            <i class="fa fa-eraser" aria-hidden="true"></i>
            <span>{{ text.detail.expensesTab.actions.clearShort || text.detail.expensesTab.actions.clear }}</span>
          </button>

          <button
            type="button"
            class="app-header-btn app-header-btn--primary"
            @click="$emit('new-expense')"
          >
            <i class="fa fa-plus" aria-hidden="true"></i>
            <span>{{ text.detail.expensesTab.actions.createShort || text.detail.expensesTab.actions.create }}</span>
          </button>
        </div>
      </div>
    </section>

    <ExpenseFeeToolbar
      class="sale-shared-expenses-toolbar"
      :filter-options="filterOptions"
      :search-placeholder="text.detail.expensesTab.toolbar.searchPlaceholder"
      :inline-filter-key="toolbarInlineFilterKey"
      :first-row-keys="toolbarFirstRowKeys"
      :second-row-keys="toolbarSecondRowKeys"
      :filter-configs="resolvedExpenseToolbarFilterConfigs"
      :all-option-labels="expenseToolbarAllOptionLabels"
      :selection-all-labels="expenseToolbarSelectionAllLabels"
      :empty-option-text="text.detail.expensesTab.toolbar.emptyOption"
      :filters-button-title="text.detail.expensesTab.toolbar.filtersButtonTitle"
      :search-button-title="text.detail.expensesTab.toolbar.searchButtonTitle"
      :date-range-presets="expenseToolbarDateRangePresets"
      initial-preset-key=""
      :first-row-columns="toolbarFirstRowColumns"
      :second-row-columns="toolbarSecondRowColumns"
      @search-change="handleSearchChange"
      @filters-change="handleFiltersChange"
    />

    <StatusTabs
      class="sale-shared-expenses-status-tabs"
      :tabs="statusTabs"
      :active-tab="activeStatusTab"
      @select="$emit('status-tab-select', $event)"
    />

    <SaleSharedExpensesTable
      :rows="rows"
      :loading="loading"
      :empty-text="emptyText"
      :pagination="pagination"
      @page-change="$emit('page-change', $event)"
      @pay-expense="$emit('pay-expense', $event)"
      @edit-expense="$emit('edit-expense', $event)"
      @delete-expense="$emit('delete-expense', $event)"
      @approve-expense="$emit('approve-expense', $event)"
      @download-xml="$emit('download-xml', $event)"
      @download-pdf="$emit('download-pdf', $event)"
      @download-receipt="$emit('download-receipt', $event)"
    />
  </section>
</template>

<script>
import { formatCurrency } from '@/utils/numberFormat';
import ExpenseFeeToolbar from '@/components/expensesFees/ExpenseFeeToolbar.vue';
import SaleSharedExpensesTable from '@/components/sales/shared/expenses/SaleSharedExpensesTable.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('sales');

export default {
  name: 'SaleSharedExpensesTab',
  components: {
    StatusTabs,
    ExpenseFeeToolbar,
    SaleSharedExpensesTable,
  },
  props: {
    saleId: {
      type: Number,
      default: null,
    },
    contextId: {
      type: Number,
      default: null,
    },
    contextIdKey: {
      type: String,
      default: 'sale_id',
    },
    toolbarInlineFilterKey: {
      type: String,
      default: 'provider',
    },
    toolbarFirstRowKeys: {
      type: Array,
      default: () => ['expense_class', 'category', 'tags'],
    },
    toolbarSecondRowKeys: {
      type: Array,
      default: () => ['has_xml', 'currency'],
    },
    toolbarFilterKeys: {
      type: Array,
      default: () => ['provider', 'expense_class', 'category', 'tags', 'has_xml', 'currency'],
    },
    toolbarFirstRowColumns: {
      type: Number,
      default: 3,
    },
    toolbarSecondRowColumns: {
      type: Number,
      default: 4,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 30,
        total_pages: 1,
        total_items: 0,
      }),
    },
    statusTabs: {
      type: Array,
      default: () => [],
    },
    activeStatusTab: {
      type: String,
      default: 'all',
    },
    summary: {
      type: Object,
      default: () => ({
        total: 0,
        paid: 0,
        pending: 0,
      }),
    },
    emptyText: {
      type: String,
      default: text.detail.expensesTab.table.empty,
    },
  },
  emits: [
    'search-change',
    'filters-change',
    'clear-expenses',
    'new-expense',
    'status-tab-select',
    'page-change',
    'pay-expense',
    'edit-expense',
    'delete-expense',
    'approve-expense',
    'download-xml',
    'download-pdf',
    'download-receipt',
  ],
  data() {
    return {
      text,
      expenseToolbarDateRangePresets: text.detail.expensesTab.toolbar.dateRangePresets,
      expenseToolbarAllOptionLabels: text.detail.expensesTab.toolbar.allOptionLabels,
      expenseToolbarSelectionAllLabels: text.detail.expensesTab.toolbar.selectionAllLabels,
      expenseToolbarFilterConfigs: [
        {
          key: 'provider',
          label: text.detail.expensesTab.toolbar.filters.provider.label,
          placeholder: text.detail.expensesTab.toolbar.filters.provider.placeholder,
          searchable: text.detail.expensesTab.toolbar.filters.provider.searchable,
        },
        {
          key: 'expense_class',
          label: text.detail.expensesTab.toolbar.filters.expenseClass.label,
          placeholder: text.detail.expensesTab.toolbar.filters.expenseClass.placeholder,
          searchable: text.detail.expensesTab.toolbar.filters.expenseClass.searchable,
        },
        {
          key: 'category',
          label: text.detail.expensesTab.toolbar.filters.category.label,
          placeholder: text.detail.expensesTab.toolbar.filters.category.placeholder,
          searchable: text.detail.expensesTab.toolbar.filters.category.searchable,
        },
        {
          key: 'tags',
          label: text.detail.expensesTab.toolbar.filters.tags.label,
          placeholder: text.detail.expensesTab.toolbar.filters.tags.placeholder,
          searchable: text.detail.expensesTab.toolbar.filters.tags.searchable,
        },
        {
          key: 'has_xml',
          label: text.detail.expensesTab.toolbar.filters.hasXml.label,
          placeholder: text.detail.expensesTab.toolbar.filters.hasXml.placeholder,
          searchable: text.detail.expensesTab.toolbar.filters.hasXml.searchable,
        },
        {
          key: 'currency',
          label: text.detail.expensesTab.toolbar.filters.currency.label,
          placeholder: text.detail.expensesTab.toolbar.filters.currency.placeholder,
          searchable: text.detail.expensesTab.toolbar.filters.currency.searchable,
        },
      ],
    };
  },
  computed: {
    resolvedContextId() {
      const explicitId = Number(this.contextId || 0) || 0;
      if (explicitId > 0) {
        return explicitId;
      }

      return Number(this.saleId || 0) || 0;
    },
    resolvedExpenseToolbarFilterConfigs() {
      const keys = Array.isArray(this.toolbarFilterKeys) ? this.toolbarFilterKeys : [];
      if (keys.length === 0) {
        return this.expenseToolbarFilterConfigs;
      }

      const allowed = new Set(keys.map((key) => String(key || '').trim()));
      return this.expenseToolbarFilterConfigs.filter((config) => allowed.has(String(config?.key || '').trim()));
    },
    normalizedSummary() {
      const toNumber = (value, fallback = 0) => {
        const parsed = Number(value);

        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const normalizeSection = (section, fallbackTotal = 0) => {
        if (!section || typeof section !== 'object') {
          const total = toNumber(fallbackTotal, 0);

          return {
            subtotal: total,
            transferred_taxes: 0,
            withheld_taxes: 0,
            total,
          };
        }

        const total = toNumber(section.total, toNumber(fallbackTotal, 0));

        return {
          subtotal: toNumber(section.subtotal, total),
          transferred_taxes: toNumber(section.transferred_taxes, toNumber(section.transfered_taxes, 0)),
          withheld_taxes: toNumber(section.withheld_taxes, 0),
          total,
        };
      };

      const totalFallback = toNumber(this.summary?.total, toNumber(this.summary?.total_expenses, 0));
      const paidFallback = toNumber(this.summary?.paid_total, toNumber(this.summary?.total_paid, toNumber(this.summary?.paid, 0)));
      const pendingFallback = toNumber(this.summary?.pending_total, toNumber(this.summary?.pending_to_pay, toNumber(this.summary?.pending, 0)));

      const totalSection = normalizeSection(
        this.summary?.expenses || this.summary?.expense || this.summary?.payment_plan,
        totalFallback
      );
      const paidSection = normalizeSection(this.summary?.paid, paidFallback);
      const pendingSection = normalizeSection(this.summary?.pending, pendingFallback);

      return {
        expenses: totalSection,
        paid: paidSection,
        pending: pendingSection,
      };
    },
    formattedSummary() {
      const formatSection = (section = {}) => ({
        subtotal: formatCurrency(Number(section.subtotal || 0) || 0),
        transferred_taxes: formatCurrency(Number(section.transferred_taxes || 0) || 0),
        withheld_taxes: formatCurrency(Number(section.withheld_taxes || 0) || 0),
        total: formatCurrency(Number(section.total || 0) || 0),
      });

      return {
        expenses: formatSection(this.normalizedSummary.expenses),
        paid: formatSection(this.normalizedSummary.paid),
        pending: formatSection(this.normalizedSummary.pending),
      };
    },
    summarySections() {
      return [
        {
          key: 'expenses',
          title: this.text.detail.expensesTab.summary.sections.expenses,
          values: this.formattedSummary.expenses,
        },
        {
          key: 'paid',
          title: this.text.detail.expensesTab.summary.sections.paidDone,
          values: this.formattedSummary.paid,
        },
        {
          key: 'pending',
          title: this.text.detail.expensesTab.summary.sections.pendingToPay,
          values: this.formattedSummary.pending,
        },
      ];
    },
  },
  methods: {
    handleSearchChange(search) {
      const payload = { search };
      const contextIdKey = String(this.contextIdKey || '').trim();
      if (contextIdKey !== '' && this.resolvedContextId > 0) {
        payload[contextIdKey] = this.resolvedContextId;
      }

      this.$emit('search-change', payload);
    },
    handleFiltersChange(filters) {
      const payload = {
        ...filters,
      };

      const contextIdKey = String(this.contextIdKey || '').trim();
      if (contextIdKey !== '' && this.resolvedContextId > 0) {
        payload[contextIdKey] = this.resolvedContextId;
      }

      this.$emit('filters-change', payload);
    },
  },
};
</script>

<style scoped>
.sale-shared-expenses-tab {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sale-shared-expenses-summary-card {
  border: 1px solid rgba(153, 152, 153, 0.24);
  border-top: 2px solid #00b2e2;
  border-radius: 9px;
  box-shadow: 0 3px 12px rgba(0, 98, 163, 0.08);
  background: #ffffff;
  margin: 0;
}

.sale-shared-expenses-summary-card .sale-shared-expenses-summary-body {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 8px;
  padding: 8px !important;
}

.sale-shared-expenses-summary-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  flex: 1;
}

.sale-shared-expenses-summary-metric {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 8px;
  min-height: 76px;
  justify-content: center;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.85), #ffffff);
}

.sale-shared-expenses-summary-metric-head {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

.sale-shared-expenses-summary-metric h4 {
  margin: 0;
  color: #1f2937;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.sale-shared-expenses-summary-breakdown {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.sale-shared-expenses-summary-breakdown-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
  padding: 3px 0;
  border: 0;
}

.sale-shared-expenses-summary-breakdown-item small {
  color: #334155;
  font-size: 9px;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.sale-shared-expenses-summary-breakdown-item strong {
  color: #0f172a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
}

.sale-shared-expenses-summary-breakdown-item--subtotal strong {
  color: #111827;
}

.sale-shared-expenses-summary-breakdown-item--transferred strong {
  color: #64748b;
}

.sale-shared-expenses-summary-breakdown-item--withheld strong {
  color: #64748b;
}

.sale-shared-expenses-summary-breakdown-item--total strong {
  color: #0b5394;
}

.sale-shared-expenses-summary-breakdown-item--total {
  border-top: 1px solid rgba(148, 163, 184, 0.45);
  margin-top: 2px;
  padding-top: 5px;
}

.sale-shared-expenses-summary-metric--paid h4 {
  color: #166534;
}

.sale-shared-expenses-summary-metric--pending h4 {
  color: #b91c1c;
}

.sale-shared-expenses-summary-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
}

.sale-shared-expenses-summary-actions .app-header-btn {
  min-height: 36px;
}

.sale-shared-expenses-toolbar,
.sale-shared-expenses-status-tabs {
  margin: 0;
}

.sale-shared-expenses-status-tabs :deep(.sales-status-tabs) {
  margin-top: 0;
}

.sale-shared-expenses-tab :deep(.expenses-table-wrap) {
  margin-top: 0;
}

@media (max-width: 991px) {
  .sale-shared-expenses-summary-metrics {
    grid-template-columns: 1fr;
  }

  .sale-shared-expenses-summary-actions {
    justify-content: flex-end;
    flex-wrap: wrap;
  }
}

@media (max-width: 767px) {
  .sale-shared-expenses-summary-breakdown-item strong {
    font-size: 12px;
  }
}
</style>
